/** WCAG 2.1 color helpers. Used by the catalog compiler (hard gate) and the editor (warnings). */

export function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** `a` moved `t` (0–1) of the way toward `b`, as #RRGGBB. */
export function mixHex(a: string, b: string, t: number): string {
  const [x, y] = [hexToRgb(a), hexToRgb(b)];
  const c = x.map((v, i) => Math.round(v + (y[i]! - v) * t));
  return "#" + c.map((v) => v.toString(16).padStart(2, "0")).join("").toUpperCase();
}

export function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

/** Pick black-ish or white text for a background, whichever contrasts more. */
export function bestOnColor(bg: string, dark = "#0F172A", light = "#FFFFFF"): string {
  return contrastRatio(bg, dark) >= contrastRatio(bg, light) ? dark : light;
}

export interface ContrastCheck {
  pair: string;
  ratio: number;
  min: number;
  pass: boolean;
}

/** The contrast pairs every design must satisfy (WCAG AA). */
export function checkDesignContrast(c: {
  primary: string; onPrimary: string; background: string; surface: string; text: string; muted: string; inverted: string; onInverted: string;
}): ContrastCheck[] {
  const pairs: Array<[string, string, string, number]> = [
    ["text / background", c.text, c.background, 4.5],
    ["text / surface", c.text, c.surface, 4.5],
    ["muted / background", c.muted, c.background, 4.5],
    ["muted / surface", c.muted, c.surface, 4.5],
    ["onPrimary / primary", c.onPrimary, c.primary, 4.5],
    ["onInverted / inverted", c.onInverted, c.inverted, 4.5],
    ["primary / background", c.primary, c.background, 3],
    ["primary / surface", c.primary, c.surface, 3],
  ];
  return pairs.map(([pair, fg, bg, min]) => {
    const ratio = Math.round(contrastRatio(fg, bg) * 100) / 100;
    return { pair, ratio, min, pass: ratio >= min };
  });
}
